/* FATCUTPRESS Visual — GRANULAR CURRENT / C2+ FINAL
 * Approved 01 GRANULAR CURRENT motion and fixed C2+ color.
 * No audio-driven particle color, background color, or brightness changes.
 */
(() => {
  'use strict';

  const canvas = document.getElementById('fatcut-bg-granular-c2plus');
  const registry = window.FATCUT_VISUAL_REGISTRY;
  if (!canvas || !registry) return;

  const gl = canvas.getContext('webgl', { alpha:false, antialias:false, depth:false, stencil:false, preserveDrawingBuffer:false });
  if (!gl) return;

  let visualActive = false;
  let raf = 0;
  let mx = .5, my = .5, pointerTarget = 0, pointerActive = 0, lastMove = 0;
  const pointerEnabled = () => document.body?.dataset.hasProducts !== 'true';

  const vs=`
attribute vec2 p;
varying vec2 v;
void main(){
  v=p*.5+.5;
  gl_Position=vec4(p,0.,1.);
}`;
  const fs=`
precision highp float;
varying vec2 v;
uniform vec2 r;
uniform vec2 m;
uniform float t;
uniform float a;
uniform int mode;

float hash21(vec2 p){
  p=fract(p*vec2(123.34,456.21));
  p+=dot(p,p+45.32);
  return fract(p.x*p.y);
}
vec2 hash22(vec2 p){
  float n=hash21(p);
  return vec2(n,hash21(p+n+19.17));
}
float noise(vec2 p){
  vec2 i=floor(p),f=fract(p);
  f=f*f*(3.-2.*f);
  return mix(
    mix(hash21(i),hash21(i+vec2(1,0)),f.x),
    mix(hash21(i+vec2(0,1)),hash21(i+vec2(1,1)),f.x),
    f.y
  );
}
float fbm(vec2 p){
  float s=0.,amp=.5;
  for(int i=0;i<4;i++){
    s+=amp*noise(p);
    p=p*2.04+vec2(5.1,9.7);
    amp*=.5;
  }
  return s;
}

float particleLayer(vec2 q,float scale,float radius,float jitter,float tm){
  vec2 gp=q*scale;
  vec2 id=floor(gp);
  vec2 f=fract(gp)-.5;
  vec2 j=(hash22(id)-.5)*jitter;
  j += .08*vec2(
    sin(tm*.7+hash21(id+3.1)*6.283),
    cos(tm*.6+hash21(id+7.3)*6.283)
  );
  float d=length(f-j);
  return 1.-smoothstep(radius,radius+.075,d);
}

void main(){
  float asp=r.x/max(r.y,1.);
  vec2 q=vec2(v.x*asp,v.y);
  vec2 mm=vec2(m.x*asp,m.y);
  float tm=t;

  float dist=length(q-mm);
  float pointer=exp(-dist*dist*18.)*a;

  vec2 flow=q;

  {
    float n=fbm(q*1.55+vec2(tm*.025,-tm*.016));
    float ang=(n-.5)*1.55 + sin(q.y*4.2-tm*.34)*.18;
    flow += vec2(cos(ang),sin(ang))*tm*.010;
    flow += vec2(
      noise(q*2.1+vec2(tm*.03,2.)),
      noise(q*2.0+vec2(7.,-tm*.025))
    )*.055;
    flow += normalize(q-mm+vec2(.0001))*pointer*.045;
  }

  float p1=particleLayer(flow,150.,.115,.74,tm);
  float p2=particleLayer(flow+vec2(.017,.011),215.,.085,.76,tm*1.11);
  float p3=particleLayer(flow+vec2(-.008,.021),310.,.060,.82,tm*.83);

  float density=clamp(p1*.63+p2*.45+p3*.34,0.,1.);

  float macro=fbm(flow*2.2+vec2(tm*.016,-tm*.012));
  float streak=.5+.5*sin((flow.x+flow.y*.48)*19.-tm*.72);
  density*=.65+.52*macro;
  density+=streak*.10;

  vec3 paper=vec3(.70,.73,.71);
  vec3 mid=vec3(.38,.40,.39);
  vec3 dark=vec3(.105,.115,.11);
  vec3 pale=vec3(.80,.84,.81);

  if(mode==0 || mode==1){
    /* C2 original palette — preserved */
    paper=mix(paper,vec3(.88,.92,.95),0.0*.94);
    pale=mix(pale,vec3(.99,1.0,1.0),0.0*.96);
    mid=mix(mid,vec3(.18,.28,.34),0.0*.88);
    dark=mix(dark,vec3(.025,.045,.055),0.0*.80);
  }else{
    /* C4 original palette — preserved */
    paper=mix(paper,vec3(.97,.985,1.0),0.0);
    pale=mix(pale,vec3(1.0,1.0,1.0),0.0);
    mid=mix(mid,vec3(.16,.29,.38),0.0*.94);
    dark=mix(dark,vec3(.015,.035,.050),0.0*.88);
  }

  vec3 col=mix(paper,mid,smoothstep(.12,.62,density));
  col=mix(col,dark,smoothstep(.48,.96,density)*.92);
  col=mix(col,pale,pointer*.22);

  /* AUDIO SILVER — apply to the FINAL image so the change cannot be hidden
     by the particle density palette above. */
  float silverDrive=clamp(max(0.0,0.0),0.,1.);
  vec3 silverTarget;
  float silverMix;

  if(mode==0){
    /* C2 ORIGINAL — unchanged from v3 */
    silverTarget=vec3(.74,.81,.85);
    silverMix=silverDrive*.58;
  }else if(mode==1){
    /* C2+ — stronger high-frequency color depth */
    float highDepth=smoothstep(.12,.62,0.0);
    silverTarget=mix(vec3(.70,.80,.86),vec3(.035,.085,.115),highDepth);
    silverMix=silverDrive*(.70+.29*highDepth);
  }else if(mode==2){
    /* C4 ORIGINAL — unchanged from v3 */
    silverTarget=vec3(.94,.97,1.0);
    silverMix=silverDrive*.92;
  }else{
    /* C4+ — same bright silver family, stronger tonal separation */
    silverTarget=vec3(.965,.985,1.0);
    silverMix=silverDrive*.94;
  }

  float lum=dot(col,vec3(.299,.587,.114));
  vec3 tinted=silverTarget*(.48+lum*.72);
  col=mix(col,tinted,clamp(silverMix,0.,.96));

  /* + modes only: expand luminance around mid-grey without changing hue family. */
  if(mode==1 || mode==3){
    float l=dot(col,vec3(.299,.587,.114));
    float contrast=(mode==1)?1.48:1.62;
    vec3 contrasted=(col-.5)*contrast+.5;
    col=mix(col,contrasted,.78);

    if(mode==1){
      float highDepth=smoothstep(.12,.64,0.0);
      float shadowMask=1.-smoothstep(.26,.82,l);
      col=mix(col,col*vec3(.12,.24,.31),shadowMask*highDepth*.96);

      /* Near-black blue steel body with metallic highlights left alive. */
      vec3 deepSteel=vec3(.018,.055,.078);
      float bodyMask=1.-smoothstep(.58,.94,l);
      col=mix(col,deepSteel*(.48+l*.58),bodyMask*highDepth*.82);

      /* Add depth in the densest particle masses, not a flat black overlay. */
      float denseMask=smoothstep(.42,.88,density);
      col*=1.-denseMask*highDepth*.24;
    }

    /* Keep highlights metallic rather than simply clipping white. */
    col += vec3(.015,.022,.028)*smoothstep(.58,.90,l)*silverDrive;
  }

  /* Highs add a clearly visible metallic lift to bright particles. */
  float metallic=smoothstep(.35,.90,density)*0.0;
  col=mix(col,vec3(.96,.985,1.0),metallic*((mode==1||mode==3)?.62:.48));

  float grain=(hash21(gl_FragCoord.xy+floor(tm*11.))-.5)*.055;
  col+=grain;

  float vign=1.-smoothstep(.28,.98,length(v-.5));
  col*=.82+.18*vign;

  /* C2+ — INVERSE OF C4
     C4 lifts the whole rendered field toward white.
     C2+ does the opposite: strong highs pull the WHOLE rendered field,
     including the non-particle background, toward deep navy/black. */
  if(mode==1){
    float highDark=smoothstep(.36,.88,0.0);
    float midDark=smoothstep(.42,.92,0.0);
    float audioDark=clamp(highDark*.22 + midDark*.16,0.,.44);
    audioDark=audioDark*audioDark*(3.-2.*audioDark);

    /* Global background/frame grade — mids now participate clearly,
       while highs still give the deepest response. */
    vec3 midnightNavy=vec3(.028,.042,.068);
    col=mix(col,midnightNavy,audioDark*.16);

    /* Increase separation after the global darkening so particles remain visible. */
    float l=dot(col,vec3(.299,.587,.114));
    vec3 navyShadow=vec3(.022,.032,.050);
    vec3 navyMid=vec3(.018,.055,.105);
    vec3 coldSilver=vec3(.32,.43,.55);

    vec3 graded=mix(navyShadow,navyMid,smoothstep(.05,.20,l));
    graded=mix(graded,coldSilver,smoothstep(.22,.48,l));
    col=mix(col,graded,audioDark*.12);

    /* Only the brightest grains retain a restrained cold highlight. */
    float spark=smoothstep(.72,.96,density)*audioDark;
    col=mix(col,vec3(.48,.60,.72),spark*.20);
  }

  col=clamp(col,0.,1.);
  gl_FragColor=vec4(col,1.);
}`;

  function shader(type, source) {
    const s=gl.createShader(type);
    gl.shaderSource(s,source);
    gl.compileShader(s);
    if(!gl.getShaderParameter(s,gl.COMPILE_STATUS)){
      console.error('[FATCUT C2+] shader',gl.getShaderInfoLog(s));
      return null;
    }
    return s;
  }
  const sv=shader(gl.VERTEX_SHADER,vs), sf=shader(gl.FRAGMENT_SHADER,fs);
  if(!sv||!sf)return;
  const pr=gl.createProgram();
  gl.attachShader(pr,sv); gl.attachShader(pr,sf); gl.linkProgram(pr);
  if(!gl.getProgramParameter(pr,gl.LINK_STATUS))return;

  const quad=gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER,quad);
  gl.bufferData(gl.ARRAY_BUFFER,new Float32Array([-1,-1,1,-1,-1,1,-1,1,1,-1,1,1]),gl.STATIC_DRAW);

  const pos=gl.getAttribLocation(pr,'p');
  const ur=gl.getUniformLocation(pr,'r');
  const um=gl.getUniformLocation(pr,'m');
  const ut=gl.getUniformLocation(pr,'t');
  const ua=gl.getUniformLocation(pr,'a');
  const uMode=gl.getUniformLocation(pr,'mode');

  function resize() {
    const dpr=Math.min(devicePixelRatio||1,1.6);
    const w=Math.max(1,Math.floor(innerWidth*dpr));
    const h=Math.max(1,Math.floor(innerHeight*dpr));
    if(canvas.width!==w||canvas.height!==h){canvas.width=w;canvas.height=h;}
  }
  addEventListener('resize',resize,{passive:true});
  resize();

  function pointerPos(e) {
    if(!pointerEnabled()) return;
    const rr=canvas.getBoundingClientRect();
    mx=Math.max(0,Math.min(1,(e.clientX-rr.left)/rr.width));
    my=1-Math.max(0,Math.min(1,(e.clientY-rr.top)/rr.height));
  }
  window.addEventListener('pointermove',e=>{
    if(!pointerEnabled()) return;
    pointerPos(e); pointerTarget=1; lastMove=performance.now();
  },{passive:true});
  window.addEventListener('pointerdown',e=>{
    if(!pointerEnabled()) return;
    pointerPos(e); pointerTarget=1; lastMove=performance.now();
  },{passive:true});
  window.addEventListener('pointerup',()=>{pointerTarget=0;},{passive:true});
  window.addEventListener('pointercancel',()=>{pointerTarget=0;},{passive:true});

  function frame(now) {
    resize();
    if(now-lastMove>700)pointerTarget*=.965;
    pointerActive+=(pointerTarget-pointerActive)*.07;

    gl.viewport(0,0,canvas.width,canvas.height);
    gl.useProgram(pr);
    gl.bindBuffer(gl.ARRAY_BUFFER,quad);
    gl.enableVertexAttribArray(pos);
    gl.vertexAttribPointer(pos,2,gl.FLOAT,false,0,0);
    gl.uniform2f(ur,canvas.width,canvas.height);
    gl.uniform2f(um,mx,my);
    gl.uniform1f(ut,now*.001);
    gl.uniform1f(ua,pointerEnabled()?pointerActive:0);
    gl.uniform1i(uMode,1); /* fixed C2+ */
    gl.drawArrays(gl.TRIANGLES,0,6);

    if(visualActive)raf=requestAnimationFrame(frame);
  }

  const adapter={
    activate(){
      visualActive=true;
      canvas.style.opacity='1';
      if(!raf)raf=requestAnimationFrame(frame);
    },
    deactivate(){
      canvas.style.opacity='0';
      window.setTimeout(()=>{
        if(canvas.style.opacity==='0'){
          visualActive=false;
          if(raf)cancelAnimationFrame(raf);
          raf=0;
        }
      },950);
    },
    update(){}
  };

  registry.add('granular_c2plus',adapter);
  registry.flush();
})();
